import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import permissionsService, { AzureADUser } from '../services/permissions.service';


interface Props {
  /** Selected Azure AD object ID (uuid) — "" means no selection. */
  value: string;
  /** Fires with the newly-selected OID (or "" if the user clears). */
  onChange: (oid: string, user: AzureADUser | null) => void;
  /** Input placeholder when empty. */
  placeholder?: string;
  /** Disable input + dropdown. */
  disabled?: boolean;
  /** Optional class to override sizing on the wrapper. */
  className?: string;
  /** When true, show email on a second line in dropdown rows (default true). */
  showEmail?: boolean;
  /** Optional id for the input element (for label/htmlFor). */
  id?: string;
  /** Restrict selectable users to emails ending with this domain (e.g. "@gwii.com"). Case-insensitive. Selected-user display is unaffected. */
  emailDomain?: string;
}


// Shared in-flight fetch + cache so mounting several pickers at once
// (e.g. Business Owner + Technical Owner) makes one network call.
let cache: AzureADUser[] | null = null;
let inflight: Promise<AzureADUser[]> | null = null;


/** Shared, cached load of the enabled-user list (also used by useUserNames). */
export async function loadUsers(): Promise<AzureADUser[]> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = permissionsService
    .getEnabledUsers()
    .then((users) => {
      cache = users;
      inflight = null;
      return users;
    })
    .catch((err) => {
      inflight = null;
      throw err;
    });
  return inflight;
}


/** Invalidate cache — call from test setup or if you've just created a user. */
export function resetUserPickerCache() {
  cache = null;
  inflight = null;
}


export default function UserPicker({
  value,
  onChange,
  placeholder = 'Search by name or email…',
  disabled = false,
  className = '',
  showEmail = true,
  id,
  emailDomain,
}: Props) {
  const [users, setUsers] = useState<AzureADUser[]>(cache ?? []);
  const [loading, setLoading] = useState<boolean>(cache === null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (cache) return;
    let cancelled = false;
    loadUsers()
      .then((rows) => {
        if (cancelled) return;
        setUsers(rows);
      })
      .catch(() => {
        if (cancelled) return;
        // Leave users empty on failure; users type into a free input.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selectedUser = useMemo(
    () => users.find((u) => u.user_id === value) ?? null,
    [users, value],
  );

  const matches = useMemo(() => {
    const domain = emailDomain ? emailDomain.toLowerCase() : null;
    const base = domain
      ? users.filter((u) => (u.email || '').toLowerCase().endsWith(domain))
      : users;
    const q = query.trim().toLowerCase();
    if (!q) return base.slice(0, 50);
    return base
      .filter((u) => {
        const name = (u.display_name || '').toLowerCase();
        const email = (u.email || '').toLowerCase();
        return name.includes(q) || email.includes(q);
      })
      .slice(0, 50);
  }, [users, query, emailDomain]);

  // Reset active index when filter changes so keyboard nav starts at the top.
  useEffect(() => { setActiveIdx(0); }, [query, open]);

  const pick = useCallback(
    (user: AzureADUser) => {
      onChange(user.user_id, user);
      setQuery('');
      setOpen(false);
    },
    [onChange],
  );

  const clear = useCallback(() => {
    onChange('', null);
    setQuery('');
    setOpen(false);
  }, [onChange]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActiveIdx((i) => Math.min(i + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && open && matches[activeIdx]) {
      e.preventDefault();
      pick(matches[activeIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  // Display mode: show a "chip" when a user is selected.
  if (selectedUser && !open) {
    return (
      <div ref={wrapperRef} className={`relative ${className}`}>
        <div
          className={`flex items-center gap-2 border rounded px-2 py-1 bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 ${
            disabled ? 'opacity-60' : ''
          }`}
        >
          <div className="flex-1 min-w-0">
            <div className="truncate text-sm text-body">
              {selectedUser.display_name || selectedUser.email}
            </div>
            {showEmail && selectedUser.email && selectedUser.display_name && (
              <div className="truncate text-xs text-muted">
                {selectedUser.email}
              </div>
            )}
          </div>
          {!disabled && (
            <>
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="text-xs text-blue-600 hover:underline dark:text-blue-400"
              >
                Change
              </button>
              <button
                type="button"
                onClick={clear}
                className="text-xs text-muted hover:text-gray-700"
                title="Clear"
              >
                ×
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Unselected (or "Change" clicked): typeahead input.
  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <input
        id={id}
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={loading ? 'Loading users…' : placeholder}
        disabled={disabled || loading}
        className="w-full border rounded px-2 py-1 bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-sm disabled:opacity-60"
        autoComplete="off"
      />
      {/* If a user was selected but the caller cleared it externally by
          resetting value to "", we still want the Change affordance to work.
          If they hit Change and then blur without picking, show a restore
          button. */}
      {value && !selectedUser && (
        <div className="mt-1 text-xs text-amber-700 dark:text-amber-300">
          Current selection has unknown OID: <code>{value}</code>
          <button
            type="button"
            onClick={clear}
            className="ml-2 text-blue-600 hover:underline dark:text-blue-400"
          >
            Clear
          </button>
        </div>
      )}
      {open && !loading && (
        <ul className="absolute z-10 mt-1 w-full max-h-72 overflow-y-auto bg-panel border border-gray-300 dark:border-gray-600 rounded shadow-lg">
          {matches.length === 0 && (
            <li className="px-3 py-2 text-sm text-muted">
              No matches.
            </li>
          )}
          {matches.map((u, i) => (
            <li key={u.user_id}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pick(u); }}
                onMouseEnter={() => setActiveIdx(i)}
                className={`w-full text-left px-3 py-1.5 text-sm ${
                  i === activeIdx
                    ? 'bg-blue-50 dark:bg-blue-900/40'
                    : 'hover:bg-highlight'
                }`}
              >
                <div className="text-body">
                  {u.display_name || u.email}
                </div>
                {showEmail && u.email && u.display_name && (
                  <div className="text-xs text-muted">
                    {u.email}
                  </div>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
